import { PRIMARY_STYLES, WEBINAR_API } from "../constants";
import { SessionActions } from "../types/actions";
import { SessionStatus, WebinarSession } from "../types/models";

const MATRIX: Record<
  SessionStatus,
  Omit<SessionActions, "primary" | "primaryLabel">
> = {
  draft: {
    canEdit: true,
    canSaveDraft: true,
    canPublish: true,
    canCancel: false,
    canDeleteDraft: true,
    canDuplicate: true,
    canReinstate: false,
  },
  scheduled: {
    canEdit: true,
    canSaveDraft: false,
    canPublish: true,
    canCancel: true,
    canDeleteDraft: false,
    canDuplicate: true,
    canReinstate: false,
  },
  live: {
    canEdit: false,
    canSaveDraft: false,
    canPublish: false,
    canCancel: false,
    canDeleteDraft: false,
    canDuplicate: true,
    canReinstate: false,
  },
  ended: {
    canEdit: false,
    canSaveDraft: false,
    canPublish: false,
    canCancel: false,
    canDeleteDraft: false,
    canDuplicate: true,
    canReinstate: false,
  },
  cancelled: {
    canEdit: false,
    canSaveDraft: false,
    canPublish: false,
    canCancel: false,
    canDeleteDraft: false,
    canDuplicate: true,
    canReinstate: true,
  },
};

const PRIMARY_BY_STATUS: Record<
  SessionStatus,
  SessionActions["primary"]
> = {
  draft: "continue_setup",
  scheduled: "copy_join_link",
  live: "live_console",
  ended: "view_report",
  cancelled: "reinstate",
};

/** Prefer backend status for edit/cancel gating; fall back to display status. */
function actionStatus(
  session: Pick<WebinarSession, "status"> & {
    backendStatus?: SessionStatus;
  }
): SessionStatus {
  return session.backendStatus ?? session.status;
}

export type GetSessionActionsOptions = {
  /**
   * Only chief coaches may create / edit / delete / duplicate.
   * Non-chiefs keep view + copy join link + share.
   * Defaults to true for backward-compatible unit tests.
   */
  canManage?: boolean;
};

/** Display lifecycle states where editing must not be offered. */
function isEditLockedByDisplayStatus(status: SessionStatus): boolean {
  return status === "live" || status === "ended" || status === "cancelled";
}

/** Display lifecycle states where cancel must not be offered. */
function isCancelLockedByDisplayStatus(status: SessionStatus): boolean {
  return status === "live" || status === "ended";
}

export function getSessionActions(
  session: Pick<WebinarSession, "status"> & {
    backendStatus?: SessionStatus;
  },
  opts: GetSessionActionsOptions = {}
): SessionActions {
  const canManage = opts.canManage !== false;
  const displayStatus = session.status;
  const status = actionStatus(session);
  const base = MATRIX[status];
  let primary: SessionActions["primary"] = PRIMARY_BY_STATUS[status];

  if (primary === "live_console" && !WEBINAR_API.canLiveConsole) {
    primary = null;
  } else if (primary === "view_report" && !WEBINAR_API.canViewReport) {
    primary = null;
  } else   if (primary === "reinstate" && !WEBINAR_API.canReinstate) {
    primary = null;
  }

  // Ended sessions must not offer copy join link (even if backend is still scheduled).
  if (displayStatus === "ended" && primary === "copy_join_link") {
    primary = null;
  }

  // Non-chief: strip mutation primaries; keep / prefer copy join link.
  if (!canManage) {
    if (
      primary === "continue_setup" ||
      primary === "reinstate" ||
      primary === "live_console" ||
      primary === "view_report"
    ) {
      primary = null;
    }
    if (
      !primary &&
      (status === "scheduled" ||
        session.status === "scheduled" ||
        session.status === "live")
    ) {
      primary = "copy_join_link";
    }

    return {
      canEdit: false,
      canSaveDraft: false,
      canPublish: false,
      canCancel: false,
      canDeleteDraft: false,
      canDuplicate: false,
      canReinstate: false,
      primary,
      primaryLabel:
        primary === "copy_join_link" ? PRIMARY_STYLES.scheduled.label : "",
    };
  }

  return {
    ...base,
    canEdit:
      base.canEdit && !isEditLockedByDisplayStatus(displayStatus),
    canPublish: base.canPublish && WEBINAR_API.canPublish,
    canCancel:
      base.canCancel &&
      WEBINAR_API.canCancel &&
      !isCancelLockedByDisplayStatus(displayStatus),
    canDeleteDraft: base.canDeleteDraft && WEBINAR_API.canDeleteDraft,
    canReinstate: base.canReinstate && WEBINAR_API.canReinstate,
    primary,
    primaryLabel: primary ? PRIMARY_STYLES[status].label : "",
  };
}

export function canTransitionTo(
  from: SessionStatus,
  to: SessionStatus
): boolean {
  if (from === to) return true;
  if (from === "draft" && to === "scheduled") return true;
  if (from === "scheduled" && to === "scheduled") return true;
  if (from === "scheduled" && to === "cancelled") return true;
  if (from === "draft" && to === "cancelled") return false;
  if (from === "cancelled" && to === "draft") return true;
  return false;
}
